'use client';

import React, {
  type CSSProperties,
  type PropsWithoutRef,
  useLayoutEffect,
  useState,
} from 'react';
import { isNumber } from '_internal/lang/isNumber';
import clsx from 'clsx';
import type { BoxProps } from 'components/Box/Box';
import { Box } from 'components/Box/Box';
import { css } from 'styled-components';
import { getScrollBarWidth } from 'utils/scrollbar-width';
import type { ThemeColorsBorder } from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';
import styles from './ScrollContainer.module.css';
import type { ShadowSize } from './Shadow';
import { Shadow } from './Shadow';
import { useScrollShadow } from './useScrollShadow';

/**
 * A cached function that determines the size of the scrollbar
 */

let size: number;

export function getShowingScrollbars() {
  if (isNumber(size)) {
    return size;
  }

  size = getScrollBarWidth();

  return size;
}

/**
 * Calculate the offset required to render the scrollbar, but keep content
 * center aligned.
 */

export function getOffsetPaddingRight(paddingRight = 0) {
  const size = getShowingScrollbars();
  return Math.max(0, paddingRight - size);
}

export type ScrollContainerProps = {
  /**
   * A required prop which is used to calculate the offset
   * required to render the scrollbar, but keep content
   * center aligned.
   */
  paddingX?: number;
  paddingY?: number;
  topShadowRunwayHeight?: number;
  shadowOffset?: number;
  enableBottomBorder?: boolean;
  enableBottomShadow?: boolean;
  enableTopBorder?: boolean;
  enableTopShadow?: boolean;
  topBorderColor?: ThemeColorsBorder;
  shadowSize?: ShadowSize;
  zIndex?: number;
  touchAction?: CSSProperties['touchAction'];
  refElAttributes?: HTMLDivElement['attributes'];
} & Omit<
  PropsWithoutRef<BoxProps>,
  'paddingLeft' | 'paddingRight' | 'paddingX' | 'paddingY'
>;

export const ScrollContainer = React.forwardRef<
  HTMLDivElement,
  ScrollContainerProps
>(
  (
    {
      paddingX = 12,
      paddingY = 0,
      topShadowRunwayHeight = 100,
      shadowOffset = 0,
      enableBottomShadow = false,
      enableBottomBorder = false,
      enableTopShadow = false,
      enableTopBorder = false,
      shadowSize = 'medium',
      topBorderColor = 'primary',
      touchAction,
      style,
      className,
      zIndex,
      height,
      children,
      backgroundColor,
      refElAttributes,
      onScroll: onScrollCallback,
      ...other
    },
    forwardedRef
  ) => {
    const { gutterSize, paddingRight, paddingLeft, shouldStyleScrollbars } =
      useScrollStyles(paddingX);

    const enableTopScrollAffordance = enableTopShadow || enableTopBorder;
    const enableBottomScrollAffordance =
      enableBottomShadow || enableBottomBorder;

    const { topOpacity, bottomOpacity, onScroll } = useScrollShadow({
      topShadowRunwayHeight,
    });

    return (
      <Box
        className={clsx(styles.wrapper, className)}
        style={{ height, ...style }}
        {...other}
      >
        <Box
          ref={forwardedRef}
          backgroundColor={backgroundColor}
          className={styles.scrollBox}
          data-custom-scrollbars={shouldStyleScrollbars || undefined}
          onScroll={(evt: React.UIEvent<HTMLDivElement, UIEvent>) => {
            onScrollCallback?.(evt);
            if (enableTopScrollAffordance || enableBottomScrollAffordance)
              onScroll(evt);
          }}
          style={
            {
              '--vapor-scroll-container-gutter-size': `${gutterSize}px`,
              zIndex,
              paddingRight: `${paddingRight}px`,
              paddingLeft: `${paddingLeft}px`,
              paddingTop: `${paddingY}px`,
              paddingBottom: `${paddingY}px`,
              // Reserves space for the border so layout doesn't shift when the Shadow component's border appears
              borderTop: enableTopBorder ? '1px solid transparent' : 'none',
              touchAction,
            } as React.CSSProperties
          }
          {...refElAttributes}
        >
          {children}
        </Box>
        {enableTopScrollAffordance && (
          <Shadow
            borderColor={topBorderColor}
            enableBorder={enableTopBorder}
            enableShadow={enableTopShadow}
            offset={shadowOffset}
            opacity={topOpacity}
            placement="top"
            shadowSize={shadowSize}
          />
        )}
        {enableBottomScrollAffordance && (
          <Shadow
            borderColor={topBorderColor}
            enableBorder={enableBottomBorder}
            enableShadow={enableBottomShadow}
            offset={shadowOffset}
            opacity={bottomOpacity}
            placement="bottom"
            shadowSize={shadowSize}
          />
        )}
      </Box>
    );
  }
);

function useScrollStyles(paddingX: number) {
  const [scrollStyles, setScrollStyles] = useState<
    ReturnType<typeof getScrollStyles>
  >({
    paddingRight: 0,
    paddingLeft: 0,
    gutterSize: 0,
    size: 0,
    shouldStyleScrollbars: false,
  });

  useLayoutEffect(() => {
    /**
     * Under the hood, `getScrollStyles` calls DOM APIs directly, so its call needs to
     * be contained within an effect in order to sync its state with the React render
     * cycle and avoid hydration errors.
     */
    setScrollStyles(getScrollStyles(paddingX));
  }, [paddingX]);

  return scrollStyles;
}

export function getScrollStyles(paddingX: number) {
  const size = getShowingScrollbars();

  const scrollBarFloating = size === 0;

  let gutterSize = 0;

  if (!scrollBarFloating) {
    gutterSize = size;
  }

  return {
    paddingRight: Math.max(0, paddingX - gutterSize),
    paddingLeft: paddingX,
    gutterSize,
    size: gutterSize,
    shouldStyleScrollbars: !scrollBarFloating,
  };
}

export type ScrollBoxProps = {
  $shouldUseCustomStyles: boolean;
  $gutterSize?: number;
};

// safari specific overrides
const customOverrides = css<any>`
  @supports selector(::-webkit-scrollbar) and (not (scrollbar-width: auto)) {
    --scrollbar-gap: 3px;
    --scrollbar-radii: 8px;
    --scrollbar-size: ${(p) => p.$gutterSize ?? 12}px;
    --scrollbar-thumb: ${theme.colors.border.tertiaryStark};
    --scrollbar-track: transparent;

    &::-webkit-scrollbar {
      height: var(--scrollbar-size);
      width: var(--scrollbar-size);
    }

    &::-webkit-scrollbar-corner {
      background-color: transparent;
    }

    &::-webkit-scrollbar-track {
      background-color: var(--scrollbar-track);
      border: var(--scrollbar-gap) solid var(--scrollbar-track);
      border-color: var(--scrollbar-track);
    }

    &::-webkit-scrollbar-thumb {
      background-clip: padding-box;
      background-color: var(--scrollbar-thumb);
      border: var(--scrollbar-gap) solid transparent;
      border-radius: var(--scrollbar-radii);
      min-height: 40px;
      transition: visibility 200ms ease;
    }
  }
`;

export const styledScrollbarMixin = css<Record<string, any>>`
  -ms-overflow-style: -ms-autohiding-scrollbar;
  -webkit-overflow-scrolling: touch;

  @supports (scrollbar-width: auto) {
    scrollbar-gutter: stable;
    scrollbar-color: ${theme.colors.border.tertiaryStark} transparent;
    scrollbar-width: auto;
  }

  ${(p) => p.$shouldUseCustomStyles && customOverrides};
`;
